import { type FC, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { Alert, Typography } from "@mui/material";
import { useVerifyEmail } from "@/admin/entities/auth";
import { ErrorMessage } from "@/shared/components";

interface VerifyEmailFormProps {
  token: string;
  onCompleted: (data: any) => void;
  onGoBack?: () => void;
  contactInfo: string;
  contactType: "email";
}

export const VerifyEmailForm: FC<VerifyEmailFormProps> = ({
  token,
  onCompleted,
  onGoBack,
  contactInfo,
  contactType,
}) => {
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(600); // 10 минут в секундах

  const [verifyEmail, { loading, error }] = useVerifyEmail({
    variables: { code, token },
    onCompleted,
  });

  // Таймер обратного отсчета
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    verifyEmail();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value.trim();
    setCode(code);

    if (code.length === 6) {
      verifyEmail({
        variables: {
          code,
          token,
        },
      });
    }
  };

  const getDescription = () => {
    if (contactType === "email") {
      return `Введите код, отправленный на ${contactInfo}`;
    }
    return `Введите SMS код, отправленный на номер ${contactInfo}`;
  };

  // Форматирование времени в MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Typography variant="body2">{getDescription()}</Typography>

        <ErrorMessage error={error} />

        <TextField
          autoFocus
          required
          fullWidth
          variant="outlined"
          label={contactType === "email" ? "Код подтверждения" : "SMS код"}
          placeholder="******"
          disabled={loading || timeLeft === 0}
          onChange={onChange}
          value={code}
        />

        {timeLeft > 0 ? (
          <Alert severity="info">
            <Typography variant="body2">
              Код действителен еще: <strong>{formatTime(timeLeft)}</strong>
            </Typography>
          </Alert>
        ) : (
          <Alert
            severity="warning"
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={onGoBack}
                disabled={loading}
              >
                Получить новый код
              </Button>
            }
          >
            <Typography variant="body2">Время действия кода истекло</Typography>
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={code.length < 6 || loading || timeLeft === 0}
        >
          Войти
        </Button>

        {onGoBack && (
          <Button
            fullWidth
            variant="text"
            color="secondary"
            onClick={onGoBack}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            Назад
          </Button>
        )}
      </Stack>
    </Box>
  );
};
